import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Beer, MapPin, Mail } from "lucide-react";
import { api } from "../lib/api";
import { CITIES, CITY_LIST } from "../lib/cities";
import VenueMap from "../components/VenueMap";
import VenueCard from "../components/VenueCard";
import { KILTRO_BRAND } from "../lib/assets";

export default function CityLanding() {
  const { slug } = useParams();
  const city = CITIES[slug];
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!city) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .listVenues({ city: city.displayName, limit: 100 })
      .then((data) => setVenues(data || []))
      .catch(() => setVenues([]))
      .finally(() => setLoading(false));
  }, [city]);

  // Center map on first matching venue, fallback to city center
  const mapCenter = useMemo(() => {
    if (venues.length > 0 && venues[0].lat && venues[0].lng) {
      return [venues[0].lng, venues[0].lat];
    }
    return city?.center || [-70.6483, -33.4569];
  }, [venues, city]);

  if (!city) {
    return <Navigate to="/descubrir" replace />;
  }

  const title = `Kiltro Scout ${city.displayName} · Cervecerías y bares craft`;
  const description = city.description;

  return (
    <div className="bg-black text-[#F5F5F5]" data-testid={`city-landing-${city.slug}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.kiltroscout.cl/ciudad/${city.slug}`} />
        <link rel="canonical" href={`https://www.kiltroscout.cl/ciudad/${city.slug}`} />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#1A1A1A]">
        <div className="kiltro-stripes h-3" />
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FEBA01] flex items-center gap-2">
            <span aria-hidden>{city.flag}</span> Kiltro Scout · {city.country}
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-7xl uppercase tracking-tight leading-[1]">
            {city.displayName} <br />
            <span className="inline-block bg-[#FEBA01] text-black px-3 py-1 -skew-x-[6deg]">
              {city.tagline}
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-[#E5E5E5] leading-relaxed">
            {city.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#A3A3A3]">
            <span>{loading ? "···" : `${venues.length} ${venues.length === 1 ? "local" : "locales"}`}</span>
            <span className="text-[#FEBA01]">·</span>
            <span>
              {city.isLive
                ? "ACTIVO"
                : <span className="text-[#FEBA01]">PRÓXIMAMENTE</span>}
            </span>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">El mapa Kiltro</div>
            <h2 className="font-display text-3xl uppercase tracking-tight">Mapa interactivo</h2>
          </div>
          {city.isLive && (
            <Link to="/descubrir" className="font-display uppercase tracking-wider text-sm text-[#FEBA01] hover:underline" data-testid="city-cta-discover">
              Ver todos los filtros →
            </Link>
          )}
        </div>
        <VenueMap venues={venues} height={500} center={mapCenter} zoom={city.zoom} />
      </section>

      {/* VENUE LIST or COMING SOON */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        {loading ? (
          <div className="text-center py-10 text-[#A3A3A3] font-mono uppercase tracking-widest text-xs">Cargando…</div>
        ) : venues.length > 0 ? (
          <>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Camada activa</div>
                <h2 className="font-display text-3xl uppercase tracking-tight">Locales en {city.displayName}</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {venues.map((v) => (
                <VenueCard key={v.id} venue={v} />
              ))}
            </div>
          </>
        ) : (
          <ComingSoonBlock city={city} />
        )}
      </section>

      {/* OTHER CITIES */}
      <section className="bg-[#0F0F0F] border-y-2 border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">La camada Kiltro crece</div>
          <h2 className="mt-1 font-display text-3xl uppercase tracking-tight">Otras ciudades</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CITY_LIST.filter((c) => c.slug !== city.slug).map((c) => (
              <Link
                key={c.slug}
                to={`/ciudad/${c.slug}`}
                data-testid={`city-link-${c.slug}`}
                className="group bg-[#1A1A1A] border-2 border-[#1A1A1A] hover:border-[#FEBA01] transition-colors p-5 flex items-center justify-between"
              >
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">
                    {c.flag} {c.country}
                  </div>
                  <div className="mt-1 font-display text-2xl uppercase tracking-tight group-hover:text-[#FEBA01]">
                    {c.displayName}
                  </div>
                  <div className="text-xs text-[#A3A3A3]">
                    {c.isLive ? "Activo" : "Próximamente"}
                  </div>
                </div>
                <ArrowRight size={18} className="text-[#FEBA01]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function ComingSoonBlock({ city }) {
  return (
    <div className="border-2 border-dashed border-[#FEBA01] p-8 text-center max-w-2xl mx-auto">
      <Beer size={32} className="mx-auto text-[#FEBA01] mb-3" />
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Próxima parada</div>
      <h3 className="mt-1 font-display text-3xl uppercase tracking-tight">
        Kiltro Scout aún no aterriza en {city.displayName}
      </h3>
      <p className="mt-3 text-sm text-[#A3A3A3]">
        ¿Tienes un bar, brewpub o cervecería en {city.displayName}?
        Sé parte de la camada y abre la ciudad con nosotros.
      </p>
      <div className="mt-5 flex flex-wrap gap-2 justify-center">
        <Link
          to="/locales"
          className="btn-brutal bg-[#FEBA01] text-black font-display text-base uppercase px-5 py-2 inline-flex items-center gap-2 shadow-brutal-white"
          data-testid={`city-coming-${city.slug}-add`}
        >
          <MapPin size={16} /> Sumar mi local
        </Link>
        <a
          href={`mailto:${KILTRO_BRAND.contactEmail}?subject=${encodeURIComponent(`Quiero Kiltro Scout en ${city.displayName}`)}`}
          className="border-2 border-[#FEBA01] text-[#FEBA01] font-display text-base uppercase px-5 py-2 inline-flex items-center gap-2 hover:bg-[#FEBA01] hover:text-black transition-colors"
        >
          <Mail size={16} /> Avisar al equipo
        </a>
      </div>
    </div>
  );
}
